/* ---------------------------------------------------------------------------
   "Edit state" — what changes where a structure is in its life, opened from
   the State page's panel header.

   Three acts, each its own form and its own button, because each is its own
   request with its own role: recording a structure already built as in
   service (LC-80, `inventory.edit`), a decision on it (LC-23,
   `status.decide`), and its underwater interval (LC-60, `inventory.edit`).
   One Save over all three would have to explain which of them it refused.

   The dialog stays open after an act: the record behind it is re-read, and
   what the act changed shows here as the fields' new values. What is typed is
   held in `stateDraft`, so a refused act keeps its reason.
   --------------------------------------------------------------------------- */

import { h } from '../core/dom.ts';
import { actions, roleCan, type State } from '../core/store.ts';
import { button, field, scrim } from '../components/widgets.ts';
import { findBridge } from '../data/derive.ts';
import { ACT_LABEL, decisionsFrom, phaseOf, type PhaseAct } from '../data/phase.ts';
import type { BridgeView } from '../data/types.ts';

const DECISION_VERB: Readonly<Partial<Record<PhaseAct, string>>> = {
  restrict: 'Restrict',
  lift: 'Lift the restriction',
  close: 'Close',
  reopen: 'Reopen',
};

/**
 * The sections this reader may use on this structure, in order. Empty means
 * there is nothing to edit, and no Edit button is drawn.
 */
export function stateEdits(b: BridgeView, s: Readonly<State>): HTMLElement[] {
  return [recordForm(b, s), decisionForm(b, s), underwaterForm(s)].filter((f): f is HTMLElement => f !== null);
}

/**
 * Whether this reader may record the structure in service, and set its
 * underwater interval. Shared with the State panel, which keeps those rows on
 * show while empty so that every field here is a row there.
 */
export function mayRecordInService(b: BridgeView, s: Readonly<State>): boolean {
  return !phaseOf(b).recorded && roleCan(s, 'inventory.edit') && s.backend === 'live';
}

export function maySetUnderwater(s: Readonly<State>): boolean {
  return roleCan(s, 'inventory.edit') && s.backend === 'live';
}

function section(title: string, onsubmit: () => void, ...children: HTMLElement[]): HTMLElement {
  return h(
    'form',
    {
      class: 'dlg__section',
      onsubmit: (e: Event) => {
        e.preventDefault();
        onsubmit();
      },
    },
    h('div', { class: 'dlg__section-title', text: title }),
    h('div', { class: 'dlg__form-grid' }, ...children),
  );
}

/**
 * Record a structure already built as in service (LC-80), from its in-service
 * day or year — the year on the record where none is typed. Offered only while
 * nothing is recorded, to whoever keeps the inventory.
 */
function recordForm(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  if (!mayRecordInService(b, s)) return null;
  return section(
    'In service',
    () => void actions.recordInService(s.stateDraft['inService'] ?? ''),
    field(
      'In service since',
      h('input', {
        id: 'state-inservice',
        class: 'input',
        type: 'text',
        inputmode: 'numeric',
        value: s.stateDraft['inService'] ?? '',
        placeholder: 'YYYY or YYYY-MM-DD',
        'aria-label': 'In service since',
        oninput: (e: Event) => actions.setStateField('inService', (e.target as HTMLInputElement).value),
      }),
      'state-inservice',
    ),
    h('div', { class: 'field', style: 'align-self:end' }, button('Record in service', { sm: true, type: 'submit', disabled: s.phaseBusy })),
  );
}

/** Restrict, lift, close or reopen, with a reason — the approver's (`status.decide`). */
function decisionForm(b: BridgeView, s: Readonly<State>): HTMLElement | null {
  const open = decisionsFrom(phaseOf(b));
  if (open.length === 0 || !roleCan(s, 'status.decide')) return null;
  const act = open.includes(s.stateDraft['act'] as PhaseAct) ? (s.stateDraft['act'] as PhaseAct) : open[0]!;
  return section(
    'Decision',
    () => void actions.decidePhase(act, s.stateDraft['reason'] ?? ''),
    field(
      'Decision',
      h(
        'select',
        {
          id: 'state-act',
          class: 'select',
          'aria-label': 'Decision',
          onchange: (e: Event) => actions.setStateField('act', (e.target as HTMLSelectElement).value),
        },
        ...open.map((a) => h('option', { value: a, selected: a === act, text: DECISION_VERB[a] ?? ACT_LABEL[a] })),
      ),
      'state-act',
    ),
    field(
      'Reason',
      h('input', {
        id: 'state-reason',
        class: 'input',
        type: 'text',
        required: true,
        value: s.stateDraft['reason'] ?? '',
        'aria-label': 'Reason',
        oninput: (e: Event) => actions.setStateField('reason', (e.target as HTMLInputElement).value),
      }),
      'state-reason',
    ),
    h('div', { class: 'field', style: 'align-self:end' }, button('Record decision', { sm: true, type: 'submit', disabled: s.phaseBusy })),
  );
}

/** The structure's underwater interval, for whoever keeps the inventory (LC-60). Empty clears it. */
function underwaterForm(s: Readonly<State>): HTMLElement | null {
  if (!maySetUnderwater(s)) return null;
  return section(
    'Underwater inspection',
    () => {
      const raw = (s.stateDraft['months'] ?? '').trim();
      void actions.setUnderwaterInterval(raw === '' ? null : Number(raw));
    },
    field(
      'Interval, months',
      h('input', {
        id: 'state-months',
        class: 'input',
        type: 'number',
        min: '1',
        max: '240',
        step: '1',
        value: s.stateDraft['months'] ?? '',
        placeholder: 'None',
        'aria-label': 'Underwater interval in months',
        oninput: (e: Event) => actions.setStateField('months', (e.target as HTMLInputElement).value),
      }),
      'state-months',
    ),
    h('div', { class: 'field', style: 'align-self:end' }, button('Set underwater interval', { sm: true, type: 'submit', disabled: s.phaseBusy })),
  );
}

/** The dialog, or null when it is closed. */
export function stateDialog(s: Readonly<State>): HTMLElement | null {
  if (!s.stateEditOpen) return null;
  const b = findBridge(s.selectedId);
  if (b === null) return null;
  const edits = stateEdits(b, s);
  const title = `Edit state of ${b.name}`;
  return h(
    'div',
    { class: 'dlg', ...scrim(() => actions.closeStateEdit(), () => s.phaseBusy) },
    h(
      'div',
      { class: 'dlg__panel', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h(
        'div',
        { class: 'dlg__head' },
        h(
          'div',
          {},
          h('div', { class: 'dlg__kicker', text: `${b.id} · state` }),
          h('h2', { class: 'dlg__title', text: 'Edit state' }),
        ),
        h('span', { class: 'spacer' }),
        h(
          'button',
          {
            type: 'button',
            class: 'dlg__close',
            title: 'Close',
            'aria-label': 'Close',
            onclick: () => actions.closeStateEdit(),
          },
          '×',
        ),
      ),
      s.phaseNote ? h('div', { class: 'dlg__note dlg__note--error' }, h('span', { text: s.phaseNote })) : null,
      h('div', { class: 'dlg__body' }, ...edits),
      h(
        'div',
        { class: 'dlg__foot' },
        h('span', { class: 'spacer' }),
        button('Close', { onClick: () => actions.closeStateEdit(), disabled: s.phaseBusy }),
      ),
    ),
  );
}
